// classifyFinding — deterministic finding classifier, ported verbatim from the
// original app (rule-engine reference §3–§5). Given a finding text and a
// transaction context, it detects the procurement route, scores the concept
// rules, and maps the winner to a register-level string. No AI; fully testable.

import { normalizeFindingText } from './normalize';
import { ROUTE_RULES } from './rulebook/ruleRules';
import type { Rule, RouteKey } from './rulebook/ruleRules';
import { referencesForRule, type RuleReferences } from './rulebook/ruleReferences';

export type Confidence = 'High' | 'Medium' | 'Low';

/** The statutory-document rule label (must match ROUTE_RULES.generic + RULE_REFERENCES). */
const STATUTORY_RULE_LABEL = 'Statutory documents not provided / verified';

export interface TxContext {
  mechanism?: string;
  value?: number;
  raw?: Record<string, unknown>;
}

export interface ClassifyResult {
  level: string;
  baseLevel: string;
  label?: string;
  anchors: string[];
  confidence: Confidence;
  route: RouteKey;
  matchedRules: string[];
  /**
   * Specific regulatory references (Eskom policy clause / PFMA section / law) for
   * the WINNING rule, when one matched. Absent for the no-rule fallbacks. Carried
   * onto the finding (persisted as JSON) so the register + dashboard can link a
   * finding to the instruments it implicates.
   */
  references?: RuleReferences;
}

export function detectProcurementRoute(tx: TxContext = {}): RouteKey {
  const mechanism = String(
    tx.mechanism ?? (tx.raw ? (tx.raw['Mechanism'] as string) : '') ?? '',
  ).toLowerCase();
  const value = Number(tx.value ?? 0);
  if (/emergency/.test(mechanism)) return 'emergency';
  if (/urgent/.test(mechanism)) return 'urgent';
  if (/informal tender|rfq|request for quotation/.test(mechanism)) return 'rfq';
  if (/lpo|local purchase/.test(mechanism)) return 'lpo';
  if (
    /formal tender|open tender|limited tender|single source|sole source|restricted tender|contract adjustment|modification/.test(
      mechanism,
    )
  ) {
    return 'tender';
  }
  if (value > 0 && value <= 30000) return 'lpo';
  if (value > 0 && value <= 1000000) return 'rfq';
  return 'tender';
}

export function scoreConceptGroup(text: string, group: string[]): number {
  const normalized = group.map((x) => normalizeFindingText(x));
  const matches = normalized.filter((term) => term && text.includes(term)).length;
  if (matches === normalized.length && matches > 0) return 4;
  if (matches >= 2) return 2;
  if (matches === 1) return 1;
  return 0;
}

export function scoreRuleMatch(text: string, rule: Rule): number {
  return rule.concepts.reduce((sum, group) => sum + scoreConceptGroup(text, group), 0);
}

export function levelWeight(level: string): number {
  return level === 'ie' ? 3 : level === 'nc' ? 2 : level === 'hk' ? 1 : 0;
}

export function mapLevelCode(level: string): string {
  if (level === 'ie') return 'Non-Compliance: Possible IE';
  if (level === 'hk') return 'Non-Compliance Housekeeping';
  return 'Non-Compliant: Policy & Procedures';
}

export function getAnchors(text: string): string[] {
  const t = String(text || '').toLowerCase();
  const anchors: string[] = [];
  if (/pfma|agsa|ocpo|constitution section 217|irregular expenditure/.test(t)) {
    anchors.push('PFMA');
  }
  if (
    /national treasury|etender|reported to nt|reporting to nt|successful tenders|unsuccessful tenders|treasury/.test(
      t,
    )
  ) {
    anchors.push('National Treasury');
  }
  if (
    /32-1034|doi|coi|nda|cft|procurement plan|procurement strategy|evaluation|adjudication|negotiation|statutory documents|eskom bulletin|opentext|edms|taxonomy/.test(
      t,
    )
  ) {
    anchors.push('Eskom Policies & Procedures');
  }
  return anchors.length ? anchors : ['Eskom Policies & Procedures'];
}

export function classifyFinding(text: string, tx: TxContext = {}): ClassifyResult {
  const normalized = normalizeFindingText(text);
  const route = detectProcurementRoute(tx);

  if (!normalized) {
    return {
      level: 'Pending Review',
      baseLevel: 'Pending Review',
      anchors: getAnchors(text),
      confidence: 'Low',
      route,
      matchedRules: [],
    };
  }
  if (/\b(not applicable|n\/?a)\b/.test(normalized)) {
    return {
      level: 'Not Applicable',
      baseLevel: 'Not Applicable',
      anchors: getAnchors(text),
      confidence: 'High',
      route,
      matchedRules: [],
    };
  }
  if (
    /\b(no findings identified|no finding identified|no gaps identified|compliant|fully compliant|resolved|closed out|no exception)\b/.test(
      normalized,
    )
  ) {
    return {
      level: 'Compliant',
      baseLevel: 'Compliant',
      anchors: getAnchors(text),
      confidence: 'High',
      route,
      matchedRules: [],
    };
  }

  const candidates: Array<{ rule: Rule; inRoute: boolean }> = [
    ...ROUTE_RULES[route].map((rule) => ({ rule, inRoute: true })),
    ...ROUTE_RULES.generic.map((rule) => ({ rule, inRoute: false })),
  ];
  const scored = candidates
    .map(({ rule, inRoute }) => ({
      rule,
      score: scoreRuleMatch(normalized, rule) + (inRoute ? 0.25 : 0),
    }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || levelWeight(b.rule.level) - levelWeight(a.rule.level));

  const bestScore = scored.length ? scored[0].score : 0;
  // Keep-threshold max(2, best-1): a rule must genuinely match concepts (score >= 2)
  // to count. A score consisting only of the in-route +0.25 bonus (no concept
  // match) leaves `top` empty, so we fall through to the regex fallbacks rather
  // than crash on an empty winner (a latent bug the original hid behind try/catch).
  const top = scored.filter((s) => s.score >= Math.max(2, bestScore - 1));
  if (top.length) {
    const winner = top.reduce(
      (best, s) => (levelWeight(s.rule.level) > levelWeight(best.rule.level) ? s : best),
      top[0],
    );
    const anchors = [...new Set(top.flatMap((s) => s.rule.anchors))];
    const matchedRules = top.slice(0, 3).map((s) => s.rule.label);
    const confidence: Confidence = bestScore >= 6 ? 'High' : bestScore >= 3 ? 'Medium' : 'Low';
    const level = mapLevelCode(winner.rule.level);
    const references = referencesForRule(winner.rule.label);
    return { level, baseLevel: level, label: winner.rule.label, anchors, confidence, route, matchedRules, references };
  }

  if (
    /\b(pfma|possible ie|irregular expenditure|national treasury|agsa|ocpo|constitution 217|award outside validity|tax non compliant|tax expired)\b/.test(
      normalized,
    )
  ) {
    return {
      level: 'Non-Compliance: Possible IE',
      baseLevel: 'Non-Compliance: Possible IE',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      confidence: 'Medium',
      route,
      matchedRules: [],
    };
  }
  if (/\b(edms|opentext|upload|filing|wrong report|administrative|housekeeping)\b/.test(normalized)) {
    return {
      level: 'Non-Compliance Housekeeping',
      baseLevel: 'Non-Compliance Housekeeping',
      anchors: getAnchors(text),
      confidence: 'Medium',
      route,
      matchedRules: [],
    };
  }
  // Guaranteed statutory anchor (Jun 11 2026 call): a statutory-document finding
  // must never fall through unanchored. Test the RAW lowercased text (not the
  // over-folded `normalized` — the bbbee→bbbbee synonym mangles B-BBEE), so
  // high-signal statutory tokens anchor to the statutory rule + its references
  // even when the concept scoring did not select a winner above.
  const lowerText = String(text || '').toLowerCase();
  if (
    /\bb-?bbee\b|\bstatutory document/.test(lowerText) ||
    /\b(csd report|csd registration|sbd4|cidb|cipc|tax clearance|tax compliance|company registration)\b/.test(
      lowerText,
    )
  ) {
    return {
      level: 'Non-Compliant: Policy & Procedures',
      baseLevel: 'Non-Compliant: Policy & Procedures',
      label: STATUTORY_RULE_LABEL,
      anchors: ['PFMA', 'Eskom Policies & Procedures', 'Statutory Requirement'],
      confidence: 'Medium',
      route,
      matchedRules: [STATUTORY_RULE_LABEL],
      references: referencesForRule(STATUTORY_RULE_LABEL),
    };
  }
  return {
    level: 'Non-Compliant: Policy & Procedures',
    baseLevel: 'Non-Compliant: Policy & Procedures',
    anchors: getAnchors(text),
    confidence: 'Low',
    route,
    matchedRules: [],
  };
}
